/**
 * chartSpec — the ```chart fence parser + ChartSpec type (#2219).
 *
 * Named distinctly from the ``ChartBlock`` component (house pattern:
 * ``MarginRankChart.tsx`` + ``marginRankChartData.ts``) — a case-only variant
 * (``chartBlock.ts`` vs ``ChartBlock.tsx``) collides on a case-insensitive
 * filesystem, where ``./ChartBlock`` resolves to the ``.ts`` parser.
 *
 * The KI-Hofassistent may answer a small series question (Erntemengen je Monat,
 * Kosten je Kategorie, Saldo je Monat) with a fenced ```chart block carrying a
 * strict, tiny JSON spec. The chat renderer draws a column chart for a VALID
 * spec and degrades EVERYTHING else to today's plain code block.
 *
 * This module is the gate, and it is PURE (no React, no recharts — recharts
 * does not render under jsdom, so the house pattern splits data-shaping from the
 * thin component so the logic stays unit-testable). Contract:
 *
 *  - it NEVER throws (a throw inside the markdown renderer would take down the
 *    whole chat bubble) — every failure path returns ``null``;
 *  - the outcome is BINARY: a fully-validated ``ChartSpec`` or ``null``. There is
 *    no partial repair (comma-decimals are NOT fixed — invalid JSON just
 *    degrades) and no clamping: an over-cap or oversized payload DEGRADES;
 *  - labels/title/unit are attacker-influenceable (prompt injection via document
 *    text), so they are kept verbatim AS DATA here and only ever rendered as
 *    React text / SVG ``<text>`` nodes downstream — never as markup.
 */

/** One plotted column. ``label`` is the category, ``value`` the finite height. */
export interface ChartPoint {
  label: string;
  value: number;
}

/** The strict spec the fence carries. ``type`` gates forward-compatibility: an
 * unknown type degrades rather than mis-renders. */
export interface ChartSpec {
  type: 'column';
  title?: string;
  unit?: string;
  points: ChartPoint[];
}

/** Hard cap on plotted points. Over-cap DEGRADES to the code block (issue
 * wording: "anything invalid or over-cap renders as the plain code block") —
 * it is NEVER clamped to the first N. */
export const MAX_CHART_POINTS = 24;

/** Raw-payload DoS guard, in UTF-16 code units (``raw.length``). A 10k-char
 * label can't blow up layout or parse cost; anything strictly larger degrades.
 * Exclusive boundary: exactly ``MAX_CHART_RAW_CHARS`` parses, one more degrades. */
export const MAX_CHART_RAW_CHARS = 10_000;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Accept a string or number label, coerced to string; reject anything else
 * (null/undefined/object/array/boolean) so a degenerate point degrades the
 * whole spec rather than rendering "[object Object]". */
function coerceLabel(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function parsePoint(raw: unknown): ChartPoint | null {
  if (!isPlainObject(raw)) return null;
  const label = coerceLabel(raw.label);
  if (label === null) return null;
  const { value } = raw;
  // Strictly a finite JSON number — a string "12" or null degrades (no coercion).
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return { label, value };
}

/**
 * Parse a raw ```chart fence body into a ChartSpec, or null to degrade.
 *
 * The caller passes the raw text between the fences (a trailing newline is
 * fine — JSON.parse tolerates surrounding whitespace). Every rejection is a
 * quiet null; this function must not throw.
 */
export function parseChartBlock(raw: string): ChartSpec | null {
  // Cheap DoS guard BEFORE JSON.parse — cap the work a hostile payload can force.
  if (typeof raw !== 'string' || raw.length > MAX_CHART_RAW_CHARS) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (!isPlainObject(parsed)) return null;
  if (parsed.type !== 'column') return null;

  const rawPoints = parsed.points;
  if (!Array.isArray(rawPoints)) return null;
  // Empty degrades (nothing to plot); over-cap degrades (NOT clamp).
  if (rawPoints.length < 1 || rawPoints.length > MAX_CHART_POINTS) return null;

  const points: ChartPoint[] = [];
  for (const entry of rawPoints) {
    const point = parsePoint(entry);
    if (point === null) return null;
    points.push(point);
  }

  // title/unit are optional decorations — kept only when they are strings,
  // otherwise ignored (they never gate validity).
  const title = typeof parsed.title === 'string' ? parsed.title : undefined;
  const unit = typeof parsed.unit === 'string' ? parsed.unit : undefined;

  return { type: 'column', title, unit, points };
}
